// @flow
import React, {Component} from 'react';
import {
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import runAfterInteractions from '../../../utils/runAfterInteractions';
import {Text, BulletPoint, Separator} from 'trpc-react-native-ui';

import type {
  AddressType,
  AgreementSpecification,
  PaymentMeanType,
} from 'digicel-kaizen-library';
import {
  Checkbox,
  ConfirmationModal,
  FullButton,
  GradientPage,
} from '../../../components/common';
import {PageTitle, PhoneNumberLine} from '../../../components/signUp';
import DeliveryAddress from '../../../components/signUp/DeliveryAddress';
import DeliveryMethod from '../../../components/signUp/DeliveryMethod';
import config from '../../../config';
import I18n from '../../../lib/i18n';
import type {
  AgreementsActionType,
  Option,
} from '../../../modules/Agreements/actions';
import type {Action as DashboardActionType} from '../../../modules/Dashboard/actions';
import type {PromotionType} from '../../../modules/SignUp/Bonus';
import type {DeliveryContactType} from '../../../modules/SignUp/DeliveryContact/reducer';
import type {DeliveryMethodChoiceType} from '../../../modules/SignUp/DeliveryMethod/reducer';
import theme from '../../../theme-config';
import {SelectedVoucherItem} from '../../Vouchers/components/SelectedVoucherItem';
import VouchersFrame from '../../Vouchers/components/VouchersFrame';
import pageStyle from '../styles';
import {SimPrice} from './components';
import ConditionsModal from './components/ConditionsModal';
import DeliveryContact from './components/DeliveryContact';
import {PlanDetails} from './components/PlanDetails';
import PromotionCodeButtonWithModal from './components/PromotionCodeButtonWithModal';
import * as Navigation from '../../../lib/navigation.js';
import WizzeeLog from '../../../lib/wizzeeLog';

class ConfirmPlan extends Component<Props, State> {
  state = {
    orderKo: false,
    toggleConfirmPlanCondition: false,
  };
  styles = getStyles();

  mockConfirmPayment = (): void => {
    this.props.mockConfirmPayment({
      braintreeNonce: 'fake-valid-visa-nonce',
    });
  };

  componentDidMount() {
    if (this.props.route.params?.isOrderingReplacementSim) {
      this.props.isOrderingReplacementSim =
        this.props.route.params.isOrderingReplacementSim;
    }
    this.props.fetchAgreements(this.props.isSignup);
    runAfterInteractions(this.props.verifyBonuses);
    if (this.props.setVoucherDetailsComeFrom) {
      if (this.props.orderType) {
        this.props.setVoucherDetailsComeFrom(this.props.orderType ?? '');
      } else if (this.props.confirmPlanType) {
        this.props.setVoucherDetailsComeFrom(this.props.confirmPlanType ?? '');
      }
    }

    if (this.props.fetchFreeTrial) {
      this.props.fetchFreeTrial();
    }

    if (
      this.props.freeTrialActivationBonus &&
      !!this.props.freeTrialActivationBonus.freeTrial
    ) {
      WizzeeLog.log(
        '>>> ConfirmPlan::componentDidMount() -> freeTrial',
        !!this.props.freeTrialActivationBonus.freeTrial,
      );
    }
  }

  componentWillUnmount() {
    if (
      !this.props.isOrderingReplacementSim &&
      this.props.swapBackupAndCurrentSubscriptionOuid
    ) {
      this.props.swapBackupAndCurrentSubscriptionOuid();
    }

    if (this.props.setCurrentVoucherDetails) {
      this.props.setCurrentVoucherDetails(null);
    }
  }

  isButtonDisabled = () => {
    if (this.props.hideCheckboxes) {
      return false;
    }
    if (this.props.isVerifyingBonuses) {
      return true;
    }
    let isButtonDisabled = false;

    if (config.displayConfirmInformationsTermsOnConfirmPlan) {
      isButtonDisabled = !this.props.toggleAgreements;
    }
    
    if (config.displayConditionOnConfirmPlan) {
      isButtonDisabled =
      isButtonDisabled || !this.state.toggleConfirmPlanCondition;
    }
    return isButtonDisabled;
  };

  renderConditionsModal = () => {
    if (this.props.displayConditionsModal) {
      return (
        <ConditionsModal
          modalHtmlFile={this.props.modalHtmlFile}
          agreementText={this.props.agreementText}
        />
      );
    }
  };

  onPressConfirmationButton = () => {
    if (this.props.eSimSelected && this.props.isOrderingReplacementSim) {
      this.props.swapWithESim();
    } else {
      this.props.submit(this.props.isOrderingReplacementSim);
    }
  };

  computedFinalPrice = (finalPrice, simPrice, eSimPrice) => {
    const {eSimSelected, confirmPlanType} = this.props;

    if (confirmPlanType === 'activation') {
      if (eSimSelected) {
        // Return Plan price + eSim price for activation order
        if (!eSimPrice) {
          throw new Error('eSIM price cannot be null or empty');
        }
        return finalPrice + eSimPrice?.taxIncludedAmount;
      } else {
        // return plan price only
        return finalPrice;
      }
    } else {
      // Return sim price only for delivery/signup order
      if (!simPrice) {
        throw new Error('SIM price cannot be null or empty');
      }
      return simPrice?.taxIncludedAmount;
    }
  };

  renderConfirmationButton = () => {
    const {
      confirmationModalBody,
      confirmButtonLabel,
      isConfirmPlanLoading,
      paymentMean,
      price,
      promotion,
      simPrice,
      eSimPrice,
      skipPayment,
      walletBalance,
      currentVoucherDetails,
    } = this.props;

    if (
      config.displayConfirmationModalOnConfirmPlan &&
      confirmationModalBody &&
      paymentMean
    ) {
      let taxInclAmountESimPrice = 0;
      if (eSimPrice) {
        taxInclAmountESimPrice = eSimPrice.taxIncludedAmount ?? 0;
      }
      const finalPlanPrice = promotion ? price - promotion.price : price;
      const isPricePositive =
        (!!taxInclAmountESimPrice && taxInclAmountESimPrice > 0) ||
        (!!simPrice?.taxIncludedAmount && simPrice?.taxIncludedAmount > 0) ||
        ((!taxInclAmountESimPrice || !simPrice?.taxIncludedAmount) &&
          finalPlanPrice > 0);

      return (
        <ConfirmationModal
          confirmContent={confirmationModalBody(
            this.computedFinalPrice(finalPlanPrice, simPrice, eSimPrice),
            paymentMean,
            walletBalance,
            currentVoucherDetails,
          )}
          disabled={this.isButtonDisabled()}
          isLoading={isConfirmPlanLoading}
          noMargin
          onConfirm={() => this.onPressConfirmationButton()}
          openModalText={I18n.t(confirmButtonLabel)}
          style={this.styles.fullbutton}
          testID={'confirmPlanButton'}
          thirdButtonAction={
            isPricePositive ? this.props.goToNewPaymentMeanPage : undefined
          }
          thirdButtonText={
            isPricePositive &&
            I18n.t('SignUp.ConfirmPlan.modal.thirdButtonText')
          }
        />
      );
    }
    return (
      <FullButton
        disabled={this.isButtonDisabled()}
        isLoading={isConfirmPlanLoading}
        onLongPress={
          skipPayment || !config.areCheatCodesEnabled
            ? undefined
            : this.mockConfirmPayment
        }
        onPress={() => this.onPressConfirmationButton()}
        style={this.styles.fullbutton}
        testID={'confirmPlanButton'}
        text={I18n.t(confirmButtonLabel)}
      />
    );
  };

  renderDeliveryAddress = () => {
    if (!!this.props.deliveryAddress && !this.props.eSimSelected) {
      return (
        <View>
          <DeliveryAddress
            deliveryAddress={this.props.deliveryAddress}
            style={this.styles.deliveryItemContainer}
          />
        </View>
      );
    }
  };

  renderESimIndications = () => {
    if (this.props.eSimSelected) {
      return (
        <View style={this.styles.eSimIndicationsContainer}>
          <BulletPoint
            text={I18n.t(
              'SignUp.ConfirmPlan.eSimIndicationsPoints.eSimIndicationPoint1',
            )}
            containerStyle={this.styles.bulletPointContainerStyle}
            bulletPointStyle={this.styles.bulletPointStyle}
            textStyle={this.styles.bulletPointTextStyle}
          />
          <BulletPoint
            text={I18n.t(
              'SignUp.ConfirmPlan.eSimIndicationsPoints.eSimIndicationPoint2',
            )}
            containerStyle={this.styles.bulletPointContainerStyle}
            bulletPointStyle={this.styles.bulletPointStyle}
            textStyle={this.styles.bulletPointTextStyle}
          />
          <Separator noMargin />
        </View>
      );
    }
  };

  renderDeliveryContact = () => {
    if (
      !!this.props.deliveryContact &&
      config.displayDeliveryContactOnConfirmPlan
    ) {
      return (
        <View>
          <DeliveryContact
            deliveryContact={this.props.deliveryContact}
            style={this.styles.deliveryItemContainer}
          />
        </View>
      );
    }
  };

  renderDeliveryMethod = () => {
    if (
      !!this.props.deliveryMethod &&
      config.displayDeliveryMethodOnConfirmPlan
    ) {
      return (
        <View>
          <DeliveryMethod
            deliveryMethod={this.props.deliveryMethod}
            style={this.styles.deliveryItemContainer}
          />
        </View>
      );
    }
  };

  renderPhonesNumbers = () => {
    if (!config.displayPhoneNumberOnConfirmPlan || !this.props.phoneNumber)
      return null;

    //Todo type as element[]
    const phoneNumbers = [
      <View key={this.props.phoneNumber}>
        <PhoneNumberLine
          phoneNumber={this.props.phoneNumber}
          isKeepingNumber={this.props.isKeepingNumber}
        />
      </View>,
    ];

    if (this.props.isKeepingNumber && this.props.activateSim) {
      const tempPhoneNumber = this.props.eSimSelected
        ? this.props.temporaryPhoneNumberWhenEsimSelected
        : this.props.temporaryPhoneNumber;
      //@ts-ignore
      phoneNumbers.push([
        <View key={tempPhoneNumber}>
          <PhoneNumberLine
            phoneNumber={tempPhoneNumber}
            isTemporaryNumber={this.props.isKeepingNumber}
          />
        </View>,
        <Text key={'text'} style={this.styles.warningText}>
          {I18n.t('SignUp.ConfirmPlan.warningTemporaryPhoneNumber')}
        </Text>,
      ]);
    }
    return phoneNumbers;
  };

  renderFreeTrialBanner = () => {
    if (this.isBonusAvailable()) {
      return (
        <>
          <View style={this.styles.freeTrialBanner}>
            <Text style={this.styles.freeTrialBannerText1}>{I18n.t('SignUp.ConfirmPlan.activationBonus.freeTrial.sectionLabel')}</Text>
            <Text style={this.styles.freeTrialBannerText2}>{I18n.t('SignUp.ConfirmPlan.activationBonus.freeTrial.sectionValue')}</Text>
          </View>
          <Separator noMargin />
        </>
      );
    }
  };

  renderPlanDetails = () => {
    if (!this.props.isOrderingReplacementSim) {
      return (
        <PlanDetails
          canEditOffer={this.props.canEditOffer}
          confirmPlanType={this.props.confirmPlanType}
          displayPricesShownAreExclusiveOfTaxMessage={
            this.props.displayPricesShownAreExclusiveOfTaxMessage
          }
          editPlanRoute={this.props.editPlanRoute}
          price={this.props.price}
        />
      );
    }
  };

  renderPromotionCodeButton = () => {
    if (this.props.canEnterReferralOrPromoCode) {
      return <PromotionCodeButtonWithModal />;
    }
  };

  renderReplacementSimImg = () => {
    if (
      this.props.isOrderingReplacementSim &&
      this.props.isOrderingReplacementSimImg
    ) {
      return (
        <View style={this.styles.imageContainer}>
          <Image
            style={
              this.props.eSimSelected
                ? this.styles.eSimSwapImage
                : this.styles.image
            }
            source={this.props.isOrderingReplacementSimImg}
            resizeMode="contain"
          />
        </View>
      );
    }
  };

  renderSimPrice = () => {
    const {eSimSelected} = this.props;
    if (
      eSimSelected ||
      (this.props.confirmPlanType !== 'activation' && config.displaySimPrice)
    ) {
      return <SimPrice withSeparator />;
    }
  };

  renderToggleAgreements = () => {
    const {agreements, toggleSelectedAgreement} = this.props;

    if (
      config.displayConfirmInformationsTermsOnConfirmPlan &&
      !this.props.hideCheckboxes &&
      agreements
    ) {
      //$FlowFixMe c le givup
      return agreements.map((agreement: AgreementSpecification) => {
        const hyperText = [
          {text: I18n.t(`agreement.${agreement.name}.label`)},
          {
            text: I18n.t(`agreement.${agreement.name}.hyperText`),
            url: config.STATIC_FILE_URL.replace(
              '{fileName}',
              `${agreement.name}.pdf`,
            ),
          },
        ];

        return (
          <Checkbox
            hyperText={hyperText}
            isChecked={agreement.isSelected}
            key={agreement.ouid}
            labelStyle={this.styles.labelStyle}
            lightColors
            onPress={() => toggleSelectedAgreement(agreement.ouid)}
            style={this.styles.checkbox}
            testID={'confirmPlanAgreementCheckbox'}
          />
        );
      });
    }
  };

  renderToggleConfirmPlanCondition = () => {
    const hyperText = [
      {text: I18n.t('SignUp.ConfirmPlan.confirmPlanCondition.part1')},
      {
        text: I18n.t('SignUp.ConfirmPlan.confirmPlanCondition.hyperText'),
        url: config.STATIC_FILE_URL.replace(
          '{fileName}',
          config.STATIC_FILE_NAME.withdrawalAndNotice,
        ),
      },
      {text: I18n.t('SignUp.ConfirmPlan.confirmPlanCondition.part2')},
    ];

    if (config.displayConditionOnConfirmPlan && !this.props.hideCheckboxes) {
      return (
        <Checkbox
          hyperText={hyperText}
          isChecked={this.state.toggleConfirmPlanCondition}
          labelStyle={this.styles.labelStyle}
          lightColors
          onPress={this.setConfirmPlanCondition}
          style={this.styles.checkbox}
          testID={'confirmPlanConditionCheckbox'}
        />
      );
    }
  };

  setConfirmPlanCondition = () =>
    this.setState({
      toggleConfirmPlanCondition: !this.state.toggleConfirmPlanCondition,
    });

  renderVouchersFrame = () => {
    const isVoucherDisplayableAndNotEmpty =
      !this.props.currentVoucherDetails &&
      this.props.filteredAvailableVouchers &&
      this.props.filteredAvailableVouchers.length > 0;

    if (isVoucherDisplayableAndNotEmpty) {
      return (
        <VouchersFrame
          comeFrom={this.props.confirmPlanType}
          parentScreen={this.defineParentScreen()}
        />
      );
    }

    return null;
  };

  defineParentScreen = () => {
    if (this.props.confirmPlanType) {
      if (this.props.confirmPlanType === 'activation') {
        return 'confirmPlanDuringActivate';
      } else if (this.props.confirmPlanType === 'signup') {
        return 'confirmPlan';
      }
    }
    return 'unknown';
  };

  onPressSelectedVoucher = () => {
    Navigation.navigate('voucherDetails', {
      voucherDetails: this.props.currentVoucherDetails,
      disabledButton: true,
    });
  };

  isBonusAvailable = () => {
    return !!this.props.freeTrialActivationBonus?.freeTrial && !this.props.isOrderingReplacementSim;
  };

  render() {
    const bottomPanelStyles: $Shape<BottomPanelStyles> = {};
    if (this.props.canEnterReferralOrPromoCode) {
      bottomPanelStyles.height = 120;
    } else {
      bottomPanelStyles.justifyContent = 'space-around';
      bottomPanelStyles.height = 70;
    }

    return (
      <GradientPage maxWidth={600} iosEdges={['top','bottom']}>
        <PageTitle
          title={I18n.t('SignUp.ConfirmPlan.title')}
          style={pageStyle.title}
        />
        <ScrollView
          showsVerticalScrollIndicator={false}
          style={this.styles.infoContainer}>
          {this.renderVouchersFrame()}
          {this.renderPhonesNumbers()}
          {this.renderPlanDetails()}
          {this.renderFreeTrialBanner()}
          {this.renderReplacementSimImg()}
          {this.renderSimPrice()}
          {this.renderDeliveryMethod()}
          {this.renderDeliveryAddress()}
          {this.renderDeliveryContact()}
          {this.renderESimIndications()}
          {this.renderToggleAgreements()}
          {this.renderToggleConfirmPlanCondition()}
        </ScrollView>
        {this.props.currentVoucherDetails ? (
          <SelectedVoucherItem
            selectedVoucher={this.props.currentVoucherDetails}
            onPressSelectedVoucher={this.onPressSelectedVoucher}
          />
        ) : null}
        <View style={{...bottomPanelStyles, ...this.styles.bottomPanel}}>
          {this.renderConditionsModal()}
          {this.renderConfirmationButton()}
          {this.renderPromotionCodeButton()}
        </View>
      </GradientPage>
    );
  }
}

const getStyles = () =>
  StyleSheet.create({
    bottomPanel: {
      backgroundColor: Platform.OS === 'web' ? theme.white : theme.bottomPanelColor,
      borderTopColor: Platform.OS === 'web' ? theme.white : theme.signUpTextColor,
      marginTop: 15,
      borderTopWidth: 1,
      paddingHorizontal: 20,
    },
    checkbox: {
      marginVertical: 10,
    },
    deliveryItemContainer: {
      marginVertical: 10,
    },
    fullbutton: {
      marginVertical: 10,
    },
    image: {
      height: 100,
      marginBottom: 29,
      marginTop: 16,
      width: 100,
    },
    eSimSwapImage: {
      height: 180,
      marginBottom: 29,
      marginTop: 16,
      width: 160,
    },
    imageContainer: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    infoContainer: {
      flex: 1,
      marginHorizontal: 17,
      marginTop: 20,
    },
    labelStyle: {
      fontSize: 12,
      fontWeight: '300',
    },
    warningText: {
      color: 'red',
      marginTop: 5,
      textAlign: 'center',
    },
    eSimIndicationsContainer: {
      marginTop: 5,
      marginBottom: 5,
    },
    bulletPointContainerStyle: {
      alignItems: 'flex-start',
      paddingRight: 20,
      paddingLeft: 21,
      marginTop: 10,
    },
    bulletPointStyle: {
      color: theme.defaultTextColor,
      marginTop: 5,
      fontSize: 10,
    },
    bulletPointTextStyle: {
      fontSize: 14,
    },
    freeTrialBanner: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      backgroundColor: 'transparent',
      paddingTop: 15,
      marginBottom: 0,
    },
    freeTrialBannerText1: {
      color: 'red',
      fontSize: 17,
      fontWeight: '500',
      textAlign: 'left',
      marginBottom: 15,
    },
    freeTrialBannerText2: {
      flex: 1,
      color: 'red',
      fontSize: 16,
      textAlign: 'right',
      marginBottom: 15,
      backgroundColor: 'transparent'
    },
  });

export type StateProps = {
  activationBonusAmount?: number | null,
  agreements: ?(AgreementSpecification[]),
  agreementText: string,
  bringBackTo: string,
  confirmButtonLabel: string,
  deliveryAddress?: AddressType,
  deliveryContact?: ?DeliveryContactType,
  deliveryMethod?: ?DeliveryMethodChoiceType,
  editPlanRoute: string,
  hideCheckboxes: boolean,
  isConfirmPlanLoading: boolean,
  isKeepingNumber: boolean,
  isOrderingReplacementSim?: ?boolean,
  isOrderingReplacementSimImg?: number,
  isReferralPromotionDiscount?: boolean,
  isSignup?: boolean,
  isVerifyingBonuses: boolean,
  modalHtmlFile: string,
  paymentMean: PaymentMeanType,
  phoneNumber: ?string,
  price: number,
  promotion: ?PromotionType,
  simPrice?: ?object,
  skipPayment: boolean,
  temporaryPhoneNumber?: string,
  temporaryPhoneNumberWhenEsimSelected?: string,
  toggleAgreements: boolean,
  walletBalance: ?number,
  freeTrialActivationBonus: ?object,
};

export type DispatchProps = {
  activateSim?: Function,
  confirmPlan: Function,
  fetchAgreements: Function,
  mockConfirmPayment: Function,
  swapBackupAndCurrentSubscriptionOuid: Function,
  verifyBonuses: Function,
  installESim: Function,
  toggleSelectedAgreement: (agreementOuid: string) => AgreementsActionType,
  fetchFreeTrial: Function,
};

export type FlaggedProps = {
  canEditOffer: boolean,
  canEnterReferralOrPromoCode: boolean,
  confirmPlanType: string,
  confirmationModalBody?: Function,
  displayConditionsModal: boolean,
  displayPricesShownAreExclusiveOfTaxMessage: boolean,
};

export type Props = StateProps &
  DispatchProps & {
    fetchAgreements: (
      isSignup?: boolean,
      options?: Option[],
    ) => AgreementsActionType,
    mockConfirmPayment: Function,
    swapBackupAndCurrentSubscriptionOuid: () => DashboardActionType,
    goToNewPaymentMeanPage: Function,
    submit: Function,
    swapWithESim: Function,
    installESim: Function,
  } & FlaggedProps;

export type State = {
  toggleConfirmPlanCondition: boolean,
  orderKo: boolean,
};

type BottomPanelStyles = {|height?: number, justifyContent?: 'space-around'|};

export default ConfirmPlan;
